'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  FileText,
  Scissors,
  Droplets,
  PenLine,
  Shuffle,
  Image,
  Images,
  FileSearch,
  FileArchive,
  Wand2,
  RefreshCw,
  Minimize2,
  Maximize2,
  Crop,
  Puzzle,
  QrCode,
  ScanLine,
  Camera,
  CreditCard,
  ScanText,
  Type,
  AlignLeft,
  Code2,
  Music,
  Scissors as AudioCut,
  Layers,
  Mic,
  Volume2,
  Gauge,
  Radio,
  AudioWaveform,
  Music2,
  ArrowRightLeft,
  Zap,
  Star,
  ChevronRight,
} from 'lucide-react';
import { Tool } from '@/lib/toolRegistry';
import { recordToolVisit } from '@/app/home/components/RecentlyUsedCarousel';

interface ToolCardProps {
  tool: Tool;
}

// Map tool IDs to Lucide icon components
const TOOL_ICON_MAP: Record<string, React.ElementType> = {
  'merge-pdf': Layers,
  'split-pdf': Scissors,
  'add-watermark': Droplets,
  'add-signature': PenLine,
  'page-reorder': Shuffle,
  'pdf-to-images': Image,
  'images-to-pdf': Images,
  'extract-pdf-text': FileSearch,
  'compress-pdf': FileArchive,
  'background-remover': Wand2,
  'image-format-converter': RefreshCw,
  'image-compress': Minimize2,
  'image-resize': Maximize2,
  'image-crop': Crop,
  'format-converter': ArrowRightLeft,
  'merge-images': Puzzle,
  'image-to-pdf': FileText,
  'qr-generator': QrCode,
  'qr-scanner': ScanLine,
  'camera-scan': Camera,
  'id-card-scan': CreditCard,
  'extract-text-ocr': ScanText,
  'word-counter': Type,
  'text-cleaner': AlignLeft,
  'case-converter': AlignLeft,
  'base64': Code2,
  'audio-converter': Music,
  'audio-trimmer': AudioCut,
  'audio-merger': Layers,
  'voice-recorder': Mic,
  'volume-booster': Volume2,
  'audio-speed-changer': Gauge,
  'pitch-changer': Radio,
  'audio-fade': AudioWaveform,
  'fade-audio': AudioWaveform,
  'ringtone-maker': Music2,
  'audio-metadata-remover': FileSearch,
  'remove-silence': Zap,
  'extract-audio': Music,
  'extract-audio-from-video': Music2,
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  beta: {
    label: 'Beta',
    className:
      'bg-amber-50/90 text-amber-600 border border-amber-200/70 backdrop-blur-sm',
  },
  'coming-soon': {
    label: 'Soon',
    className:
      'bg-slate-50/90 text-slate-400 border border-slate-200/60 backdrop-blur-sm',
  },
};

// Derive a soft gradient background for the icon badge from the tool's color
function getIconGradient(color: string): string {
  const gradients: Record<string, string> = {
    '#DC2626': 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    '#2563EB': 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    '#7C3AED': 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
    '#059669': 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    '#D97706': 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
  };
  return gradients[color] ?? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)';
}

function getIconGradientDark(color: string): string {
  const gradients: Record<string, string> = {
    '#DC2626': 'linear-gradient(135deg, rgba(239,68,68,0.20) 0%, rgba(220,38,38,0.12) 100%)',
    '#2563EB': 'linear-gradient(135deg, rgba(59,130,246,0.20) 0%, rgba(37,99,235,0.12) 100%)',
    '#7C3AED': 'linear-gradient(135deg, rgba(139,92,246,0.20) 0%, rgba(109,40,217,0.12) 100%)',
    '#059669': 'linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(5,150,105,0.12) 100%)',
    '#D97706': 'linear-gradient(135deg, rgba(245,158,11,0.20) 0%, rgba(217,119,6,0.12) 100%)',
  };
  return gradients[color] ?? 'linear-gradient(135deg, rgba(14,165,233,0.20) 0%, rgba(2,132,199,0.12) 100%)';
}

export default function ToolCard({ tool }: ToolCardProps) {
  const badge = STATUS_BADGE[tool.status];
  const isDisabled = tool.status === 'coming-soon';
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    if (!isDisabled) {
      recordToolVisit(tool.id);
    }
  };

  const IconComponent = TOOL_ICON_MAP[tool.id] ?? Star;
  const iconGradient = isDark ? getIconGradientDark(tool.color) : getIconGradient(tool.color);

  const card = (
    <div
      className={`
        relative overflow-hidden flex flex-col h-full
        rounded-3xl p-5
        transition-all duration-300 ease-out
        ${isDisabled
          ? 'opacity-60 cursor-default' :'cursor-pointer group hover:-translate-y-1.5 active:scale-[0.975] active:translate-y-0 active:shadow-none'
        }
      `}
      style={{
        background: isDark ? 'rgba(30,41,59,0.75)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.60)',
        boxShadow: isDark
          ? '0 4px 20px rgba(0,0,0,0.25), 0 1px 4px rgba(0,0,0,0.15)'
          : '0 4px 20px rgba(37,99,235,0.07), 0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* Hover glow ring */}
      <div
        className={`
          pointer-events-none absolute inset-0 rounded-3xl
          transition-opacity duration-300
          ${isDisabled ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}
        `}
        style={{
          boxShadow: isDark
            ? 'inset 0 0 0 1.5px rgba(96,165,250,0.40)'
            : 'inset 0 0 0 1.5px rgba(37,99,235,0.35)',
        }}
      />

      {/* Status badge */}
      {badge && (
        <span
          className={`absolute top-3 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            isDark
              ? 'bg-slate-700/80 text-slate-400 border border-slate-600/60'
              : badge.className
          }`}
        >
          {badge.label}
        </span>
      )}

      {/* Icon badge */}
      <div
        className={`
          w-12 h-12 rounded-2xl flex items-center justify-center mb-4 flex-shrink-0
          transition-all duration-300
          ${isDisabled ? '' : 'group-hover:scale-110 group-hover:shadow-md'}
        `}
        style={{
          background: iconGradient,
          boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.20)' : '0 2px 8px rgba(0,0,0,0.07)',
        }}
      >
        <IconComponent
          size={22}
          strokeWidth={1.8}
          style={{ color: tool.color }}
        />
      </div>

      {/* Title */}
      <h3
        className={`
          font-semibold text-sm leading-tight mb-1.5
          transition-colors duration-200
          ${isDisabled
            ? isDark ? 'text-slate-500' : 'text-slate-500'
            : isDark
              ? 'text-slate-200 group-hover:text-blue-300' :'text-slate-800 group-hover:text-blue-600'
          }
        `}
      >
        {tool.title}
      </h3>

      {/* Description */}
      <p
        className="text-xs leading-relaxed line-clamp-2 mb-4 flex-1"
        style={{ color: isDark ? '#64748b' : '#64748b' }}
      >
        {tool.description}
      </p>

      {/* Open Tool Button */}
      {isDisabled ? (
        <div
          className="w-full py-2 px-4 rounded-xl text-xs font-semibold text-center cursor-default select-none"
          style={{
            background: isDark ? 'rgba(30,41,59,0.80)' : 'rgba(226,232,240,0.80)',
            color: isDark ? '#475569' : '#94a3b8',
          }}
        >
          Coming Soon
        </div>
      ) : (
        <div
          className="
            w-full py-2 px-4 rounded-xl text-white text-xs font-semibold
            flex items-center justify-center gap-1.5
            transition-all duration-200
            group-hover:shadow-[0_4px_16px_rgba(37,99,235,0.40)] group-hover:brightness-110
            group-active:scale-95 group-active:brightness-95
          "
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 55%, #1d4ed8 100%)',
            boxShadow: '0 2px 8px rgba(37,99,235,0.22)',
          }}
        >
          Open Tool
          <ChevronRight size={13} strokeWidth={2.5} className="transition-transform duration-200 group-hover:translate-x-0.5" />
        </div>
      )}
    </div>
  );

  if (isDisabled) return <div className="h-full">{card}</div>;
  return (
    <Link href={tool.route} onClick={handleClick} className="block group h-full">
      {card}
    </Link>
  );
}
